// src/components/StageCard.jsx
//
// One reusable card for a stage table (Permit, Fieldwork, Site_Data,
// Final_Report, Digital_data, Artifact_submission). Per PROJECT_TRACKER_PLAN.md,
// every stage table shares the same status/assignee/timestamp shape by
// design, so this is one parameterized component rather than six
// near-identical ones -- ProjectDetailPage renders it six times with
// different config.
//
// Config props (all required unless noted):
//   title            "Permit", "Fieldwork", etc.
//   projectId        Project.id -- every stage endpoint is keyed by this
//   basePath         API path prefix, e.g. "/tracker/permits"
//   statusField      model field holding the main status, e.g. "status" or
//                    "application_status"
//   statusChoices    STATUS_CHOICES or FIELDWORK_STATUS_CHOICES
//   extraFields      [{ key, label, type }] -- free-text/date fields the
//                    claim mechanism doesn't touch (permit_no, due_date...),
//                    edited inline and saved with a plain PATCH
//   hasRevision      (optional) also render a second StatusBlock for the
//                    revision_* trio (Site_Data, Final_Report only)
//   creatable        (optional) if the GET 404s, show a "doesn't exist yet"
//                    state with a create form instead of an error -- only
//                    Permit is 0-or-1, every other stage is auto-provisioned
//                    with the project so this should never trigger for them
//
// Every stage also gets a collapsed-by-default Notes section (backed by
// each stage table's new `notes` field) -- not configurable per stage,
// since all six got the same field. Deliberately not shown on
// ProjectListPage or either dashboard, just here on the detail page.

import { useEffect, useState } from "react";
import { apiFetch } from "../api/client";
import { createAndClaim } from "../api/stageActions";
import StatusBlock from "./StatusBlock";

function emptyValues(fields) {
  const values = {};
  fields.forEach((f) => {
    values[f.key] = "";
  });
  return values;
}

export default function StageCard({
  title,
  projectId,
  basePath,
  statusField,
  statusChoices,
  extraFields = [],
  hasRevision = false,
  creatable = false,
}) {
  // Read fresh on every mount rather than once at module load -- these are
  // module-level constants no longer, on purpose. StageCard/ProjectListPage
  // used to read localStorage at the top of the file, which only runs once
  // when the JS bundle first evaluates (before login, on a first visit).
  // Since login navigates client-side (no full page reload, see
  // LoginPage.jsx), that stale read stuck currentUsername/isAdmin at
  // null/false for the rest of the session -- hiding Unclaim/status-change
  // controls even for the user's own claims. Reading here instead means it
  // re-runs every time this component mounts, which happens on every
  // route change.
  const currentUsername = localStorage.getItem("username");
  const isAdmin = localStorage.getItem("role") === "admin";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notFound, setNotFound] = useState(false);

  const [editing, setEditing] = useState(false);
  const [editValues, setEditValues] = useState(emptyValues(extraFields));
  const [saving, setSaving] = useState(false);

  const [createValues, setCreateValues] = useState(emptyValues(extraFields));
  const [creating, setCreating] = useState(false);
  const [quickClaiming, setQuickClaiming] = useState(false);

  // Notes -- collapsed by default (per Jerome: not on the list/dashboards,
  // just here on the detail page, and expandable rather than always shown
  // since a note can be a full paragraph). Uses the same
  // data-is-the-source-of-truth pattern as extraFields above: notesValue
  // only tracks the in-progress edit, setData(row) after a successful
  // PATCH is what actually updates what's displayed.
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);

  const load = () => {
    setLoading(true);
    setError("");
    setNotFound(false);
    apiFetch(`${basePath}/${projectId}/`)
      .then((row) => {
        setData(row);
        const values = {};
        extraFields.forEach((f) => {
          values[f.key] = row[f.key] ?? "";
        });
        setEditValues(values);
        setNotesValue(row.notes || "");
      })
      .catch((err) => {
        if (creatable) {
          setNotFound(true);
        } else {
          setError(err.message || "Couldn't load.");
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(load, [basePath, projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError("");
    try {
      const payload = { project: projectId };
      extraFields.forEach((f) => {
        payload[f.key] = createValues[f.key] || null;
      });
      const row = await apiFetch(`${basePath}/`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      setData(row);
      setNotFound(false);
    } catch (err) {
      setError(err.message || "Couldn't create.");
    } finally {
      setCreating(false);
    }
  };

  const handleSaveFields = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = {};
      extraFields.forEach((f) => {
        payload[f.key] = editValues[f.key] || null;
      });
      const row = await apiFetch(`${basePath}/${projectId}/`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      setData(row);
      setEditing(false);
    } catch (err) {
      setError(err.message || "Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  const handleQuickClaim = async () => {
    setQuickClaiming(true);
    setError("");
    try {
      const row = await createAndClaim(basePath, projectId);
      setData(row);
      setNotFound(false);
    } catch (err) {
      setError(err.message || "Couldn't claim.");
    } finally {
      setQuickClaiming(false);
    }
  };

  const handleSaveNotes = async (e) => {
    e.preventDefault();
    setSavingNotes(true);
    setError("");
    try {
      const row = await apiFetch(`${basePath}/${projectId}/`, {
        method: "PATCH",
        body: JSON.stringify({ notes: notesValue || null }),
      });
      setData(row);
      setEditingNotes(false);
    } catch (err) {
      setError(err.message || "Couldn't save notes.");
    } finally {
      setSavingNotes(false);
    }
  };

  const postAction = async (actionPath, body) => {
    const row = await apiFetch(`${basePath}/${projectId}/${actionPath}/`, {
      method: "POST",
      body: body ? JSON.stringify(body) : undefined,
    });
    setData(row);
  };

  if (loading) {
    return (
      <div className="card stage-card">
        <h3>{title}</h3>
        <p>Loading...</p>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="card stage-card">
        <h3>{title}</h3>
        <p className="stage-card-empty">No {title.toLowerCase()} yet.</p>
        <div className="form-actions">
          <button type="button" onClick={handleQuickClaim} disabled={quickClaiming || creating}>
            {quickClaiming ? "Claiming..." : "Claim"}
          </button>
        </div>
        <p className="stage-card-or">or fill in what you already know:</p>
        <form onSubmit={handleCreate} className="stacked-form">
          {extraFields.map((f) => (
            <div key={f.key}>
              <label>{f.label}</label>
              <input
                type={f.type}
                value={createValues[f.key]}
                onChange={(e) =>
                  setCreateValues((v) => ({ ...v, [f.key]: e.target.value }))
                }
              />
            </div>
          ))}
          {error && <p className="form-error">{error}</p>}
          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={creating || quickClaiming}>
              {creating ? "Creating..." : `Create ${title.toLowerCase()}`}
            </button>
          </div>
        </form>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="card stage-card">
        <h3>{title}</h3>
        <p className="form-error">{error}</p>
      </div>
    );
  }

  return (
    <div className="card stage-card">
      <h3>{title}</h3>

      <StatusBlock
        status={data[statusField]}
        assignedToUsername={data.assigned_to_username}
        statusChangedAt={data.status_changed_at}
        statusChoices={statusChoices}
        currentUsername={currentUsername}
        isAdmin={isAdmin}
        onClaim={() => postAction("claim")}
        onUnclaim={() => postAction("unclaim")}
        onSetStatus={(value) => postAction("set-status", { status: value })}
      />

      {hasRevision && (
        <StatusBlock
          label="Revision"
          status={data.revision_status}
          assignedToUsername={data.revision_assigned_to_username}
          statusChangedAt={data.revision_status_changed_at}
          statusChoices={statusChoices}
          currentUsername={currentUsername}
          isAdmin={isAdmin}
          onClaim={() => postAction("claim-revision")}
          onUnclaim={() => postAction("unclaim-revision")}
          onSetStatus={(value) => postAction("set-revision-status", { status: value })}
        />
      )}

      {extraFields.length > 0 && (
        <div className="stage-card-fields">
          {!editing && (
            <>
              <table className="fields-table">
                <tbody>
                  {extraFields.map((f) => (
                    <tr key={f.key}>
                      <td className="fields-table-label">{f.label}</td>
                      <td>{data[f.key] || "--"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button type="button" onClick={() => setEditing(true)}>
                Edit details
              </button>
            </>
          )}
          {editing && (
            <form onSubmit={handleSaveFields} className="stacked-form">
              {extraFields.map((f) => (
                <div key={f.key}>
                  <label>{f.label}</label>
                  <input
                    type={f.type}
                    value={editValues[f.key]}
                    onChange={(e) =>
                      setEditValues((v) => ({ ...v, [f.key]: e.target.value }))
                    }
                  />
                </div>
              ))}
              <div className="form-actions">
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? "Saving..." : "Save"}
                </button>
                <button type="button" onClick={() => setEditing(false)} disabled={saving}>
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      <div className="stage-notes">
        <button
          type="button"
          className="stage-notes-toggle"
          onClick={() => setNotesExpanded((e) => !e)}
        >
          Notes {data.notes && <span className="stage-notes-dot" />}
          <span className="stage-notes-caret">{notesExpanded ? "▴" : "▾"}</span>
        </button>

        {notesExpanded && !editingNotes && (
          <>
            <p className="stage-notes-text">{data.notes || "No notes yet."}</p>
            <button type="button" onClick={() => setEditingNotes(true)}>
              Edit notes
            </button>
          </>
        )}

        {notesExpanded && editingNotes && (
          <form onSubmit={handleSaveNotes} className="stacked-form">
            <textarea
              rows={4}
              value={notesValue}
              onChange={(e) => setNotesValue(e.target.value)}
            />
            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={savingNotes}>
                {savingNotes ? "Saving..." : "Save"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditingNotes(false);
                  setNotesValue(data.notes || "");
                }}
                disabled={savingNotes}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>

      {error && <p className="form-error">{error}</p>}
    </div>
  );
}
